import _ from 'lodash';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';

interface ManualConditionState extends BaseToolPersistedState {
  color: string;
  isExecuting: boolean;
}

export type ManualConditionDehydratedState = Omit<ManualConditionState, 'isExecuting'>;

export class ManualConditionStore extends BaseToolStore {
  static readonly storeName = 'sqManualConditionStore';
  type = TREND_TOOLS.MANUAL_CONDITION;

  initialize(): void {
    const initialState: ManualConditionState = {
      ...BASE_TOOL_COMMON_PROPS,
      color: '',
      isExecuting: false,
    };
    this.state = this.immutable(initialState);
  }

  get color(): string {
    return this.state.get('color');
  }

  get isExecuting(): boolean {
    return this.state.get('isExecuting');
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @return {Object} State for the store
   */
  dehydrate(): ManualConditionDehydratedState {
    return _.omit(this.state.serialize(), ['isExecuting']);
  }

  /**
   * Sets the references panel state
   *
   * @param {Object} dehydratedState - Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState: Partial<ManualConditionDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  /**
   * Removes properties from config which are stored as part of the formula.
   *
   * @param config - The state that will be saved to UIConfig
   * @return The modified config
   */
  override modifyConfigParams<TConfig extends Record<string, unknown>>(config: TConfig): TConfig {
    return _.omit(config, ['maximumDuration', 'advancedParametersCollapsed']);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
    MANUAL_CONDITION_SET_COLOR: ({ color }: { color: string }) => {
      this.state.set('color', color);
    },
    MANUAL_CONDITION_SET_IS_EXECUTING: ({ isExecuting }: { isExecuting: boolean }) => {
      this.state.set('isExecuting', isExecuting);
    },
  };
}
